import React, { useState, useEffect } from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
  FormControl, InputLabel, Select, MenuItem, Snackbar, Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

export default function MeetingRooms() {
  const [meetingRooms, setMeetingRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [currentRoom, setCurrentRoom] = useState({
    id: '',
    name: '',
    capacity: '',
    location: '',
    description: '',
    equipment: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchMeetingRooms();
  }, []);

  const fetchMeetingRooms = async () => {
    try {
      const response = await axios.get('https://pdks.bytebridge.com.tr/api/meeting-rooms');
      setMeetingRooms(response.data);
    } catch (error) {
      console.error('Error fetching meeting rooms:', error);
      showSnackbar('Toplantı odaları yüklenirken hata oluştu', 'error');
    }
  };

  const handleOpen = (room = null) => {
    if (room) {
      setCurrentRoom(room);
      setEditing(true);
    } else {
      setCurrentRoom({
        id: '',
        name: '',
        capacity: '',
        location: '',
        description: '',
        equipment: ''
      });
      setEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentRoom(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`https://pdks.bytebridge.com.tr/api/meeting-rooms/${currentRoom.id}`, currentRoom);
        showSnackbar('Toplantı odası başarıyla güncellendi', 'success');
      } else {
        await axios.post('https://pdks.bytebridge.com.tr/api/meeting-rooms', currentRoom);
        showSnackbar('Toplantı odası başarıyla eklendi', 'success');
      }
      fetchMeetingRooms();
      handleClose();
    } catch (error) {
      console.error('Error saving meeting room:', error);
      showSnackbar('Toplantı odası kaydedilirken hata oluştu', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu toplantı odasını silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`https://pdks.bytebridge.com.tr/api/meeting-rooms/${id}`);
        showSnackbar('Toplantı odası başarıyla silindi', 'success');
        fetchMeetingRooms();
      } catch (error) {
        console.error('Error deleting meeting room:', error);
        showSnackbar('Toplantı odası silinirken hata oluştu', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Toplantı Odaları</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Yeni Toplantı Odası Ekle
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Oda Adı</TableCell>
              <TableCell>Kapasite</TableCell>
              <TableCell>Konum</TableCell>
              <TableCell>Açıklama</TableCell>
              <TableCell>Ekipman</TableCell>
              <TableCell>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {meetingRooms.map((room) => (
              <TableRow key={room.id}>
                <TableCell>{room.name}</TableCell>
                <TableCell>{room.capacity}</TableCell>
                <TableCell>{room.location}</TableCell>
                <TableCell>{room.description}</TableCell>
                <TableCell>{room.equipment}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(room)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(room.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editing ? 'Toplantı Odasını Düzenle' : 'Yeni Toplantı Odası Ekle'}</DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              label="Oda Adı"
              name="name"
              value={currentRoom.name}
              onChange={handleChange}
              required
            />
            <TextField
              margin="normal"
              fullWidth
              type="number"
              label="Kapasite"
              name="capacity"
              value={currentRoom.capacity}
              onChange={handleChange}
              required
            />
            <TextField
              margin="normal"
              fullWidth
              label="Konum"
              name="location"
              value={currentRoom.location}
              onChange={handleChange}
              required
            />
            <TextField
              margin="normal"
              fullWidth
              multiline
              rows={2}
              label="Açıklama"
              name="description"
              value={currentRoom.description}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Ekipmanlar"
              name="equipment"
              value={currentRoom.equipment}
              onChange={handleChange}
              placeholder="Projeksiyon, Beyaz Tahta, TV, vb."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>İptal</Button>
            <Button type="submit" variant="contained" color="primary">
              {editing ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
